import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { 
    CheckCircleIcon, ClockIcon, ExternalLinkIcon, 
    XIcon, ExclamationIcon, LinkIcon, EyeIcon, UserIcon 
} from '../../components/icons/Icons';

// --- HELPERS ---
const getFinancialYear = (dateObj) => {
    if (!dateObj) return '';
    const date = new Date(dateObj);
    const month = date.getMonth(); 
    const year = date.getFullYear();
    if (month < 3) return `${year - 1}-${year}`;
    return `${year}-${year + 1}`;
};

const ensureAbsoluteUrl = (url) => {
    if (!url) return '#';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `https://${url}`;
};

const UserAvatar = ({ url, name, className = "h-10 w-10" }) => {
    const [imgError, setImgError] = useState(false);
    if (url && !imgError) {
        return (
            <img 
                src={url} alt={name} 
                className={`${className} rounded-full object-cover border border-gray-200`} 
                onError={() => setImgError(true)}
            />
        );
    }
    return (
        <div className={`${className} rounded-full bg-blue-100 flex items-center justify-center text-[#213368] font-bold text-xs shrink-0 overflow-hidden border border-blue-200`}>
            {name ? name.charAt(0).toUpperCase() : <UserIcon className="h-4 w-4" />}
        </div>
    );
};

export default function TeamOversightPanel({ userProfile, appId }) {
    const [teamMembers, setTeamMembers] = useState([]);
    const [teamActivities, setTeamActivities] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters & Modals
    const [selectedYear, setSelectedYear] = useState(getFinancialYear(new Date()));
    const [yearOptions, setYearOptions] = useState([getFinancialYear(new Date())]); 
    
    // UI State
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [itemToReject, setItemToReject] = useState(null);
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);

    // 1. Fetch Team Data
    useEffect(() => {
        const fetchTeamData = async () => {
            setLoading(true);
            try {
                // A. Fetch Direct Reports
                const usersRef = collection(db, `artifacts/${appId}/users`);
                const qUsers = query(
                    usersRef, 
                    where('managerId', '==', userProfile.id),
                    where('status', '==', 'Approved')
                );
                const userSnap = await getDocs(qUsers);
                
                const teamList = userSnap.docs.map(d => ({ 
                    id: d.id, 
                    name: d.data().name, 
                    photoURL: d.data().photoURL,
                    department: d.data().department 
                }));
                setTeamMembers(teamList);

                if (teamList.length === 0) {
                    setLoading(false);
                    return;
                }

                // B. Fetch Activities (Updated Query)
                // ✅ FIX: Query strictly by 'managerId' to satisfy Security Rules.
                // This replaces the old 'userId in chunks' method.
                const activitiesRef = collection(db, `artifacts/${appId}/pd_activities`);
                const qActivities = query(
                    activitiesRef, 
                    where('managerId', '==', userProfile.id), // <--- CRITICAL FIX
                    orderBy('createdAt', 'desc')
                );
                
                const snap = await getDocs(qActivities);
                const allActivities = snap.docs.map(d => {
                    const data = d.data();
                    const date = data.date ? data.date.toDate() : data.createdAt.toDate();
                    return {
                        id: d.id,
                        ...data,
                        activityDate: date,
                        fy: getFinancialYear(date)
                    };
                });
                
                setTeamActivities(allActivities);

                // Set Years
                const years = new Set([getFinancialYear(new Date())]); 
                allActivities.forEach(a => { if(a.fy) years.add(a.fy); });
                const sortedYears = Array.from(years).sort().reverse();
                setYearOptions(sortedYears);
                
                if (!sortedYears.includes(selectedYear)) {
                    setSelectedYear(sortedYears[0]);
                }

            } catch (error) {
                console.error("Error fetching team data:", error);
            }
            setLoading(false);
        };

        fetchTeamData();
    }, [appId, userProfile.id]);

    // --- DERIVED DATA ---
    const pendingItems = useMemo(() => {
        const pending = teamActivities.filter(a => 
            a.status === 'Pending Approval' || a.status === 'Pending Verification'
        );
        return pending.map(item => {
            const user = teamMembers.find(m => m.id === item.userId);
            return {
                ...item,
                photoURL: user ? user.photoURL : null 
            };
        });
    }, [teamActivities, teamMembers]);

    const scorecardData = useMemo(() => {
        const data = {};
        teamMembers.forEach(m => {
            data[m.id] = { 
                ...m, 
                Technical: 0, 'Soft Skills': 0, 'Peer Learning': 0, Knowledge: 0, Total: 0 
            };
        });

        teamActivities.forEach(a => {
            // Ensure we only count completed items for the selected year
            if (a.status === 'Completed' && a.fy === selectedYear && data[a.userId]) {
                const type = a.type === 'Technical' ? 'Technical' :
                             a.type === 'Soft Skills' ? 'Soft Skills' :
                             a.type === 'Peer Learning' ? 'Peer Learning' : 'Knowledge';
                const hours = Number(a.hours) || 0;
                data[a.userId][type] += hours;
                data[a.userId].Total += hours;
            }
        });
        return Object.values(data);
    }, [teamActivities, teamMembers, selectedYear]);

    // --- ACTIONS ---
    const initiateReject = (item) => {
        setItemToReject(item);
        setRejectModalOpen(true);
    };

    const handleApproveOrVerify = async (item, action) => {
        if (!window.confirm(`Confirm ${action}?`)) return;
        const newStatus = action === 'Approve' ? 'Approved' : 'Completed';
        
        try {
            const docRef = doc(db, `artifacts/${appId}/pd_activities`, item.id);
            await updateDoc(docRef, {
                status: newStatus,
                rejectionReason: null,
                managerActionDate: serverTimestamp()
            });
            setTeamActivities(prev => prev.map(a => a.id === item.id ? { ...a, status: newStatus } : a));
        } catch (error) {
            console.error(error);
            alert("Action failed.");
        }
    };

    const openMemberHistory = (memberId) => {
        const member = teamMembers.find(m => m.id === memberId);
        const memberActs = teamActivities.filter(a => a.userId === memberId); 
        setSelectedMember({ info: member, activities: memberActs });
        setHistoryModalOpen(true);
    };

    if (loading) return <div className="p-8 text-center text-gray-500">Loading team data...</div>;

    if (teamMembers.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-center bg-gray-50 rounded-xl border border-dashed border-gray-300">
                <div className="bg-white p-4 rounded-full mb-3 shadow-sm">
                   <ClockIcon className="h-8 w-8 text-gray-300" />
                </div>
                <h3 className="text-lg font-bold text-[#213368]">No Direct Reports</h3>
                <p className="text-sm text-gray-500 mt-1 max-w-sm">
                    You don't have any active team members assigned to you.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            
            {/* 1. APPROVAL QUEUE */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <div>
                        <h3 className="font-bold text-[#213368] flex items-center"><ClockIcon className="h-5 w-5 mr-2 text-orange-500" /> Pending Actions</h3>
                        <p className="text-xs text-gray-500">Approvals & Verifications</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${pendingItems.length > 0 ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                        {pendingItems.length} Pending
                    </span>
                </div>

                {pendingItems.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 text-sm">All caught up!</div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {pendingItems.map(item => (
                            <div key={item.id} className="p-4 hover:bg-gray-50 transition flex flex-col sm:flex-row justify-between items-start gap-4">
                                <div className="flex items-start space-x-3 w-full">
                                    <UserAvatar url={item.photoURL} name={item.userName} className="h-10 w-10" />
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2">
                                            <span className="font-bold text-[#213368] text-sm">{item.userName}</span>
                                            <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase font-bold ${item.status === 'Pending Approval' ? 'bg-yellow-100 text-yellow-700' : 'bg-purple-100 text-purple-700'}`}>
                                                {item.status}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-800 font-bold mt-1">{item.title}</p>
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-1">
                                            <span className="bg-gray-100 px-1.5 py-0.5 rounded">{item.type}</span>
                                            <span>{item.provider}</span>
                                            <span>{item.hours} Hrs</span>
                                            {item.cost > 0 && <span className="font-mono text-gray-700">{item.currency} {item.cost}</span>}
                                        </div>
                                        <div className="flex space-x-4 mt-2">
                                            {item.link && (
                                                <a href={ensureAbsoluteUrl(item.link)} target="_blank" rel="noreferrer" className="flex items-center text-xs text-blue-600 hover:text-blue-800 font-medium">
                                                    <LinkIcon className="h-3 w-3 mr-1" /> Course Link
                                                </a>
                                            )}
                                            {item.status === 'Pending Verification' && item.proofLink && (
                                                <a href={ensureAbsoluteUrl(item.proofLink)} target="_blank" rel="noreferrer" className="flex items-center text-xs text-purple-600 hover:text-purple-800 font-medium">
                                                    <ExternalLinkIcon className="h-3 w-3 mr-1" /> View Proof
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex space-x-2 w-full sm:w-auto justify-end sm:self-center">
                                    {item.status === 'Pending Approval' ? (
                                        <>
                                            <button onClick={() => initiateReject(item)} className="px-3 py-1.5 border border-red-200 text-red-600 hover:bg-red-50 rounded text-xs font-bold transition">Reject</button>
                                            <button onClick={() => handleApproveOrVerify(item, 'Approve')} className="px-3 py-1.5 bg-[#213368] text-white hover:opacity-90 rounded text-xs font-bold transition">Approve</button>
                                        </>
                                    ) : (
                                        <>
                                            <button onClick={() => initiateReject(item)} className="px-3 py-1.5 border border-red-200 text-red-600 hover:bg-red-50 rounded text-xs font-bold transition">Reject</button>
                                            <button onClick={() => handleApproveOrVerify(item, 'Verify')} className="px-3 py-1.5 bg-green-600 text-white hover:bg-green-700 rounded text-xs font-bold transition flex items-center">
                                                <CheckCircleIcon className="h-3 w-3 mr-1" /> Verify
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* 2. TEAM SCORECARD */}
            <div>
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-[#213368]">Team Scorecard</h3>
                    <div className="flex items-center space-x-2">
                        <span className="text-xs font-bold text-gray-500 uppercase">Year:</span>
                        <select className="text-sm border-gray-300 rounded-md shadow-sm focus:ring-[#20BEC6] focus:border-[#20BEC6]" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
                            {yearOptions.map(y => <option key={y} value={y}>FY {y}</option>)}
                        </select>
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-left text-sm">
                            <thead className="bg-gray-50 text-gray-500 font-medium uppercase text-xs tracking-wider border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-3">Employee</th>
                                    <th className="px-6 py-3 text-right text-teal-700">Technical</th>
                                    <th className="px-6 py-3 text-right text-orange-700">Soft Skills</th>
                                    <th className="px-6 py-3 text-right text-blue-700">Knowledge</th>
                                    <th className="px-6 py-3 text-right text-red-700">Peer</th>
                                    <th className="px-6 py-3 text-right font-bold text-[#213368] bg-gray-100">Total</th>
                                    <th className="px-6 py-3"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {scorecardData.map((member, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50 transition cursor-pointer" onClick={() => openMemberHistory(member.id)}>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center">
                                                <UserAvatar url={member.photoURL} name={member.name} className="h-8 w-8 mr-3" />
                                                <div>
                                                    <div className="font-bold text-[#213368]">{member.name}</div>
                                                    <div className="text-xs text-gray-400">{member.department || 'Unassigned'}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right text-gray-600">{member.Technical}</td>
                                        <td className="px-6 py-4 text-right text-gray-600">{member['Soft Skills']}</td>
                                        <td className="px-6 py-4 text-right text-gray-600">{member.Knowledge}</td>
                                        <td className="px-6 py-4 text-right text-gray-600">{member['Peer Learning']}</td>
                                        <td className="px-6 py-4 text-right font-bold text-[#213368] bg-gray-50/50">{member.Total}</td>
                                        <td className="px-6 py-4 text-right">
                                            <button className="text-gray-400 hover:text-[#20BEC6]">
                                                <EyeIcon className="h-5 w-5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* MODALS */}
            {rejectModalOpen && (
                <RejectionModal 
                    appId={appId} item={itemToReject}
                    onClose={() => { setRejectModalOpen(false); setItemToReject(null); }}
                    onSuccess={(id) => {
                         const newStatus = itemToReject.status === 'Pending Verification' ? 'Proof Rejected' : 'Rejected';
                         setTeamActivities(prev => prev.map(a => a.id === id ? { ...a, status: newStatus } : a));
                    }}
                />
            )}

            {historyModalOpen && selectedMember && (
                <MemberHistoryModal 
                    member={selectedMember.info}
                    activities={selectedMember.activities}
                    selectedYear={selectedYear}
                    onClose={() => { setHistoryModalOpen(false); setSelectedMember(null); }}
                />
            )}
        </div>
    );
}

// ... RejectionModal and MemberHistoryModal remain unchanged from your original ...
function RejectionModal({ appId, item, onClose, onSuccess }) {
    const [reason, setReason] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const newStatus = item.status === 'Pending Verification' ? 'Proof Rejected' : 'Rejected';
            const docRef = doc(db, `artifacts/${appId}/pd_activities`, item.id);
            await updateDoc(docRef, {
                status: newStatus,
                rejectionReason: reason,
                managerActionDate: serverTimestamp()
            });
            onSuccess(item.id);
            onClose();
        } catch (error) {
            console.error(error);
            alert("Error rejecting request");
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
                <div className="flex items-center text-red-600 mb-4">
                    <ExclamationIcon className="h-6 w-6 mr-2" />
                    <h3 className="text-lg font-bold">Reject Request</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">Please provide a reason for rejecting <strong>{item.title}</strong>.</p>
                <form onSubmit={handleSubmit}>
                    <textarea 
                        className="w-full border border-gray-300 p-3 rounded-lg text-sm focus:ring-2 focus:ring-red-500 outline-none mb-4"
                        rows="3" placeholder="Reason..." value={reason} onChange={e => setReason(e.target.value)} required
                    />
                    <div className="flex justify-end space-x-3">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-gray-500 text-sm font-medium">Cancel</button>
                        <button type="submit" disabled={isSaving} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700">
                            {isSaving ? 'Processing...' : 'Confirm Rejection'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function MemberHistoryModal({ member, activities, selectedYear, onClose }) {
    const filteredActs = activities.filter(a => a.fy === selectedYear).sort((a, b) => b.createdAt - a.createdAt);
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[80vh] flex flex-col">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-xl">
                    <div className="flex items-center space-x-3">
                        <UserAvatar url={member.photoURL} name={member.name} className="h-12 w-12" />
                        <div><h3 className="text-xl font-bold text-[#213368]">{member.name}</h3><p className="text-sm text-gray-500">Training History - FY {selectedYear}</p></div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XIcon className="h-6 w-6" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-6">
                    {filteredActs.length === 0 ? <p className="text-center text-gray-400 italic mt-10">No activities recorded for this year.</p> : (
                        <table className="min-w-full text-left text-sm">
                            <thead className="bg-white text-gray-500 font-medium uppercase text-xs border-b border-gray-200">
                                <tr><th className="py-2">Activity</th><th className="py-2">Type</th><th className="py-2 text-right">Hrs</th><th className="py-2 pl-4">Status</th></tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredActs.map(act => (
                                    <tr key={act.id} className="hover:bg-gray-50">
                                        <td className="py-3 pr-2"><div className="font-bold text-[#213368]">{act.title}</div><div className="text-xs text-gray-500">{act.provider}</div></td>
                                        <td className="py-3"><span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">{act.type}</span></td>
                                        <td className="py-3 text-right font-medium">{act.hours}</td>
                                        <td className="py-3 pl-4"><span className={`text-[10px] uppercase font-bold px-2 py-1 rounded-full ${act.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{act.status}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}